import { useCallback, useEffect, useState } from 'react'
import { useLazyQuery } from '@apollo/client'
import { useRecoilValue } from 'recoil'
import { useProvince } from '@/services/address/useProvince'
import { useDistrict } from '@/services/address/useDistrict'
import { useSubDistrict } from '@/services/address/useSubDistrict'
import { userGql } from '@/gql'
import healthClaimFormAtom from '@/recoil/healthClaimForm/atom'
import { HealthClaimModeEnum } from '@/constant/E_CLAIM'
import { useHealthClaimForm } from '@/contexts/healthClaimForm'

const CompensationRequestByChequeInputGroupHook = () => {
  const [backofficeGetUserPolicyByPolicyNumber, { loading: isLoadingPolicyData }] = useLazyQuery(
    userGql.query.backofficeGetUserPolicyByPolicyNumber,
    {
      fetchPolicy: 'no-cache',
    },
  )

  const healthClaim = useRecoilValue(healthClaimFormAtom)
  const isCreateOrEditMode =
    healthClaim.mode === HealthClaimModeEnum.CREATE || healthClaim.mode === HealthClaimModeEnum.EDIT

  const healthClaimForm = useHealthClaimForm()
  const {
    forms: { compensationRequestForm },
    selectedPolicy,
    selectedPolicyNumber,
  } = healthClaimForm

  const normalizedSelectedPolicyNumber = selectedPolicyNumber || ''
  const selectedInsuredName = `${selectedPolicy?.insuredFirstName ?? ''} ${
    selectedPolicy?.insuredLastName ?? ''
  }`.trim()

  const [isUseAddressFromAddressPolicy, setIsUseAddressFromAddressPolicy] = useState(false)
  const [selectedProvinceCode, setSelectedProvinceCode] = useState<string>('')
  const [selectedDistrictCode, setSelectedDistrictCode] = useState<string>('')
  const [selectedSubDistrictCode, setSelectedSubDistrictCode] = useState<string>('')

  const isRequired = { required: true, message: '' }

  // Fetch address data
  const { data: provinces = [], isLoading: isProvinceLoading } = useProvince()
  const { data: districts = [], isLoading: isDistrictLoading } = useDistrict(selectedProvinceCode)
  const { data: subDistricts = [], isLoading: isSubDistrictLoading } =
    useSubDistrict(selectedDistrictCode)

  // Transform data to Select options format
  const provinceOptions = provinces.map((province) => ({
    label: province.displayNameTh,
    value: province.provinceCode,
  }))

  const districtOptions = districts.map((district) => ({
    label: district.displayNameTh,
    value: district.districtCode,
  }))

  const subDistrictOptions = subDistricts.map((subDistrict) => ({
    label: subDistrict.displayNameTh,
    value: subDistrict.subDistrictCode,
    postCode: subDistrict.postCode,
  }))

  // Auto-fill postal code options based on selected sub-district
  const postalOptions = selectedSubDistrictCode
    ? subDistricts
        .filter((sd) => sd.subDistrictCode === selectedSubDistrictCode)
        .map((sd) => ({
          label: sd.postCode,
          value: sd.postCode,
        }))
    : []

  // Handle province change
  const handleProvinceChange = (value: unknown) => {
    const provinceCode = value as string
    setSelectedProvinceCode(provinceCode)
    setSelectedDistrictCode('')
    setSelectedSubDistrictCode('')
    // Clear dependent fields
    compensationRequestForm.setFieldsValue({
      district: undefined,
      subdistrict: undefined,
      postal: undefined,
    })
  }

  // Handle district change
  const handleDistrictChange = (value: unknown) => {
    const districtCode = value as string
    setSelectedDistrictCode(districtCode)
    setSelectedSubDistrictCode('')
    // Clear dependent fields
    compensationRequestForm.setFieldsValue({
      subdistrict: undefined,
      postal: undefined,
    })
  }

  // Handle sub-district change
  const handleSubDistrictChange = (value: unknown) => {
    const subDistrictCode = value as string
    setSelectedSubDistrictCode(subDistrictCode)
    // Auto-fill postal code
    const selectedSubDistrict = subDistricts.find((sd) => sd.subDistrictCode === subDistrictCode)
    if (selectedSubDistrict) {
      compensationRequestForm.setFieldValue('postal', selectedSubDistrict.postCode)
    }
  }

  // Fetch insured address by policy number if using address from policy
  const getInsuredAddressByPolicyNumber = useCallback(
    async (policyNumber: string) => {
      const policy = await backofficeGetUserPolicyByPolicyNumber({ variables: { policyNumber } })

      if (policy.data?.backofficeGetUserPolicyByPolicyNumber) {
        const insuredAddress = policy.data.backofficeGetUserPolicyByPolicyNumber.insuredAddress
        const insuredDistrictCode =
          policy.data.backofficeGetUserPolicyByPolicyNumber.insuredDistrict.districtCode
        const insuredProvinceCode =
          policy.data.backofficeGetUserPolicyByPolicyNumber.insuredProvince.provinceCode

        return {
          insuredAddress,
          insuredDistrictCode,
          insuredProvinceCode,
        }
      }
    },
    [backofficeGetUserPolicyByPolicyNumber],
  )

  // Set address fields from policy data if checked
  const handleSetIsUseAddressFromAddressPolicy = useCallback(
    (value: boolean) => {
      setIsUseAddressFromAddressPolicy(value)

      if (value) {
        // Set insured name when using policy address
        compensationRequestForm.setFieldsValue({
          compensationPersonName: selectedInsuredName,
        })

        // Fetch address data based on selected policy number
        getInsuredAddressByPolicyNumber(normalizedSelectedPolicyNumber).then((addressData) => {
          if (addressData?.insuredDistrictCode && addressData?.insuredProvinceCode) {
            compensationRequestForm.setFieldsValue({
              compensationAddress: addressData.insuredAddress,
              province: addressData.insuredProvinceCode,
              district: addressData.insuredDistrictCode,
              subdistrict: '', // Reset sub-district
              postal: '', // Postal code will be set based on sub-district selection
            })
            setSelectedProvinceCode(addressData.insuredProvinceCode)
            setSelectedDistrictCode(addressData.insuredDistrictCode)
            setSelectedSubDistrictCode('') // Reset sub-district
          }
        })
      } else {
        // Reset fields only when user unchecks the checkbox
        compensationRequestForm.setFieldsValue({
          compensationPersonName: '',
          compensationAddress: '',
          province: undefined,
          district: undefined,
          subdistrict: undefined,
          postal: undefined,
        })
        setSelectedProvinceCode('')
        setSelectedDistrictCode('')
        setSelectedSubDistrictCode('')
      }
    },
    [
      compensationRequestForm,
      getInsuredAddressByPolicyNumber,
      normalizedSelectedPolicyNumber,
      selectedInsuredName,
    ],
  )

  // Initialize selected address codes from form values on mount
  useEffect(() => {
    const formValues = compensationRequestForm.getFieldsValue()

    if (formValues.province) setSelectedProvinceCode(formValues.province)
    if (formValues.district) setSelectedDistrictCode(formValues.district)
    if (formValues.subdistrict) setSelectedSubDistrictCode(formValues.subdistrict)
  }, [compensationRequestForm])

  return {
    // State
    isCreateOrEditMode,
    isUseAddressFromAddressPolicy,
    selectedProvinceCode,
    selectedDistrictCode,
    selectedSubDistrictCode,
    isRequired,

    // Loading states
    isLoadingPolicyData,
    isProvinceLoading,
    isDistrictLoading,
    isSubDistrictLoading,

    // Options
    provinceOptions,
    districtOptions,
    subDistrictOptions,
    postalOptions,

    // Handlers
    handleProvinceChange,
    handleDistrictChange,
    handleSubDistrictChange,
    handleSetIsUseAddressFromAddressPolicy,
  }
}

export default CompensationRequestByChequeInputGroupHook
